import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Button, Typography, Alert } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useOtpVerification } from '../../application/useOtpVerification';
import AuthCardLayout from './AuthCardLayout';

/** UC-5: usa useOtpVerification (T28). Máquina request/verify. */
export default function OtpVerifyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    step,
    email,
    setEmail,
    code,
    setCode,
    error,
    success,
    loading,
    requestCode,
    verifyCode,
    requestNewCode,
  } = useOtpVerification();

  const handleRequest = async (e: FormEvent) => {
    e.preventDefault();
    await requestCode();
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    const verified = await verifyCode();
    if (verified) navigate('/dashboard', { replace: true });
  };

  return (
    <AuthCardLayout>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Lock sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" fontWeight={700}>
          {t('auth.otpVerification')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          {step === 'request' ? t('auth.enterEmailForOtp') : t('auth.enterOtpCode')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {step === 'request' ? (
        <form onSubmit={handleRequest}>
          <TextField
            fullWidth
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            disabled={loading}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mt: 2, py: 1.5 }}
          >
            {loading ? t('auth.sending') : t('auth.requestCode')}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleVerify}>
          <TextField
            fullWidth
            label={t('auth.otpCode')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            margin="normal"
            disabled={loading}
            slotProps={{
              htmlInput: {
                maxLength: 6,
                style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 },
              },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mt: 2, py: 1.5 }}
          >
            {loading ? t('auth.verifying') : t('auth.verify')}
          </Button>
          <Button fullWidth sx={{ mt: 1 }} onClick={requestNewCode}>
            {t('auth.requestNewCode')}
          </Button>
        </form>
      )}

      <Button fullWidth sx={{ mt: 2 }} onClick={() => navigate('/login')}>
        {t('auth.backToLogin')}
      </Button>
    </AuthCardLayout>
  );
}
